/**
 * The paste dialog's choices, pure: each control turns one decision into
 * the next request's `choices` — read against the review on screen, since
 * an absent field means the server's automatic answer (v2: the twin).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenSection, GroupCopyChoices, GroupCopyReview } from '@trafficpulse/shared';

type Rows = Pick<GroupCopyReview, 'sections' | 'containers'>;

const toggle = <T>(taken: T[], item: T, take: boolean): T[] =>
  take ? (taken.includes(item) ? taken : [...taken, item]) : taken.filter((x) => x !== item);

/** One section: the copy's values (take) or the target master config's own. */
export function withSection(choices: GroupCopyChoices, review: Rows, key: DropGenSection, take: boolean): GroupCopyChoices {
  const taken = review.sections.filter((s) => s.take).map((s) => s.key);
  return { ...choices, sections: toggle(taken, key, take) };
}

/** Take all = the automatic answer (every section); Keep all = none of them. */
export function withAllSections(choices: GroupCopyChoices, take: boolean): GroupCopyChoices {
  const { sections: _drop, ...rest } = choices;
  return take ? rest : { ...rest, sections: [] };
}

export function withContainer(choices: GroupCopyChoices, review: Rows, sourceId: string, take: boolean): GroupCopyChoices {
  const taken = review.containers.filter((c) => c.take).map((c) => c.sourceId);
  return { ...choices, containerIds: toggle(taken, sourceId, take) };
}

/** A source gateway → a target gateway, or null to leave its lanes and pins out. */
export function withGateway(choices: GroupCopyChoices, sourceRouteId: number, target: number | null): GroupCopyChoices {
  return { ...choices, gateways: { ...choices.gateways, [String(sourceRouteId)]: target } };
}

/** A source list group → a target one, GROUP_COPY_CREATE, or null to leave it out. */
export function withListGroup(choices: GroupCopyChoices, sourceId: string, target: string | null): GroupCopyChoices {
  return { ...choices, listGroups: { ...choices.listGroups, [sourceId]: target } };
}

export function withCampaign(choices: GroupCopyChoices, sourceWiserId: number, target: number): GroupCopyChoices {
  return { ...choices, campaigns: { ...choices.campaigns, [String(sourceWiserId)]: target } };
}

/** v2 — a source list → a target list, or null to leave its pins out. */
export function withList(choices: GroupCopyChoices, sourceWiserId: number, target: number | null): GroupCopyChoices {
  return { ...choices, lists: { ...choices.lists, [String(sourceWiserId)]: target } };
}

/** v2 — a gateway's lanes arrive without their sender IDs (clear) or with them (the default). */
export function withClearedSenders(choices: GroupCopyChoices, sourceRouteId: number, clear: boolean): GroupCopyChoices {
  const others = (choices.clearSenders ?? []).filter((id) => id !== sourceRouteId);
  const next = clear ? [...others, sourceRouteId] : others;
  const { clearSenders: _drop, ...rest } = choices;
  return next.length > 0 ? { ...rest, clearSenders: next } : rest;
}

/** Another target group: its master config and campaigns differ — those answers start over. */
export function forNewTarget(choices: GroupCopyChoices): GroupCopyChoices {
  const { sections: _s, campaigns: _c, ...rest } = choices;
  return rest;
}
